import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { STATUS_TONES } from "../../components/actuals/BatchDetail";
import { fmtSEK } from "../../components/planning/revenue/format";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE") : "—");

export default function ActualsHistoryPage() {
  const { user } = useAuth();
  const canWrite = user?.role !== "READ_ONLY_MANAGEMENT";
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [batches, setBatches] = useState(null);

  useEffect(() => {
    api.get("/companies")
      .then((c) => {
        setCompanies(c.data);
        if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/periods/years`)
      .then((res) => {
        setYears(res.data);
        const pick = pickActiveYear(res.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    if (!companyId) return;
    setBatches(null);
    api.get("/actuals/batches", { params: { company_id: companyId, ...(year ? { year } : {}) } })
      .then((res) => setBatches(res.data))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(load, [companyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (b) => {
    try {
      await api.delete(`/actuals/batches/${b.id}`);
      toast.success("Draft import deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="actuals-history-page">
      <PageHeader
        title="Import History"
        subtitle="Every accounting P&L import is preserved — superseded batches are never deleted."
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="history-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="history-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`history-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        }
      />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="history-table">
        {!batches ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : batches.length === 0 ? (
          <EmptyState title="No imports yet" description="Import the monthly accounting P&L under Actuals → Import P&L." testId="history-empty" />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Period</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">File</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Status</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Rows</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Reported PBT</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Imported</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-4">Committed</th>
                <th className="py-2.5 px-4" />
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 hover:bg-accent/40" data-testid={`history-row-${b.id}`}>
                  <td className="py-2.5 px-4 text-sm font-medium text-slate-900">{b.year}-{String(b.month).padStart(2, "0")}</td>
                  <td className="py-2.5 px-4 text-sm text-slate-600">{b.file_name}</td>
                  <td className="py-2.5 px-4">
                    <StatusBadge tone={STATUS_TONES[b.status]} testId={`history-status-${b.id}`}>{b.status}</StatusBadge>
                    {b.status === "SUPERSEDED" && b.superseded_comment && (
                      <span className="block text-[11px] text-slate-400 mt-0.5" title={b.superseded_comment}>
                        {b.superseded_comment.length > 40 ? `${b.superseded_comment.slice(0, 40)}…` : b.superseded_comment}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-sm text-right text-slate-600">{b.row_count}</td>
                  <td className="py-2.5 px-4 text-sm text-right text-slate-900">{b.parsed?.reported_pbt == null ? "—" : `${fmtSEK(b.parsed.reported_pbt)} kr`}</td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">{fmtDate(b.created_at)}</td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">{fmtDate(b.committed_at)}</td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" className="h-7 text-xs"
                        onClick={() => navigate(`/actuals/import?batch=${b.id}`)}
                        data-testid={`history-view-${b.id}`}>
                        View
                      </Button>
                      {canWrite && b.status === "IMPORTED" && (
                        <Button variant="outline" size="sm" className="h-7 text-xs text-[#B91C1C]"
                          onClick={() => remove(b)} data-testid={`history-delete-${b.id}`}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
